/**
 * CancelRescheduleModal
 * ──────────────────────────────────────────────────────────────────
 * Real-mode actionable modal for an existing appointment. Two flows:
 *
 *   • CANCEL    → PATCH /api/scheduler/appointments/{id}/cancel
 *                 body { reason, notify_patient }
 *
 *   • RESCHEDULE→ PUT   /api/scheduler/appointments/{id}
 *                 body { start, end?, entity_id?, location_id? }
 *
 * Backend endpoints already exist (vendor §18). NO new API shape
 * invented for this modal.
 *
 * Role gating
 *   - Both writes require `canSchedulerWrite` from `capabilitiesFor`.
 *     When false the action buttons are visible but disabled, and a
 *     small inline note explains why; opening the modal in a read-
 *     only role is still allowed (callers may want to view-only).
 *
 * Toast / error mapping (per spec)
 *   - 403 → "Permission denied — your role cannot perform this action."
 *   - 409 → "Slot conflict — pick a different time."
 *   - 422 → "Cannot apply this transition." (e.g. terminal → reschedule)
 *   - 5xx → "Server error — please retry."
 *   - else→ generic "Action failed."
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Card, CardContent } from '../../ui/card';
import { toast } from 'sonner';
import { X, Calendar, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { capabilitiesFor } from '../../../lib/permissions';
import * as schedulerApi from '../../../lib/api/schedulerApi';

const toLocalInput = (iso) => {
  if (!iso) return '';
  // Produce a "YYYY-MM-DDTHH:MM" string suitable for <input type="datetime-local">
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const toIsoString = (local) => {
  if (!local) return '';
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
};

const toastApiError = (err, fallback = 'Action failed.') => {
  const status = err?.status;
  const msg = err?.message || fallback;
  if (status === 403) {
    toast.error('Permission denied — your role cannot perform this action.', { description: msg });
  } else if (status === 409) {
    toast.error('Slot conflict — pick a different time.', { description: msg });
  } else if (status === 422) {
    toast.error('Cannot apply this transition.', { description: msg });
  } else if (status === 404) {
    toast.error('Appointment not found — it may have been cancelled or removed.', { description: msg });
  } else if (status && status >= 500) {
    toast.error('Server error — please retry.', { description: msg });
  } else {
    toast.error(fallback, { description: msg });
  }
};

const CancelRescheduleModal = ({ open, onClose, appointment, onMutated }) => {
  const { user } = useAuth();
  const caps = useMemo(() => capabilitiesFor(user || {}), [user]);
  const canWrite = !!caps.canSchedulerWrite;

  const [tab, setTab] = useState('reschedule'); // 'reschedule' | 'cancel'
  const [reason, setReason] = useState('');
  const [notifyPatient, setNotifyPatient] = useState(true);
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTab('reschedule');
    setReason('');
    setNotifyPatient(true);
    setNewStart(toLocalInput(appointment?.start));
    setNewEnd(toLocalInput(appointment?.end));
    setSubmitting(false);
  }, [open, appointment]);

  const handleCancel = useCallback(async () => {
    if (!appointment?.id) return;
    if (submitting) return;
    setSubmitting(true);
    const res = await schedulerApi.cancelAppointment(appointment.id, {
      reason: reason || null,
      notify_patient: notifyPatient,
    });
    setSubmitting(false);
    if (!res?.ok) {
      toastApiError(res?.error, 'Could not cancel appointment.');
      return;
    }
    toast.success('Appointment cancelled.', {
      description: notifyPatient ? 'Patient will be notified.' : undefined,
    });
    onMutated?.(res.data?.appointment);
    onClose?.();
  }, [appointment, reason, notifyPatient, submitting, onMutated, onClose]);

  const handleReschedule = useCallback(async () => {
    if (!appointment?.id) return;
    if (submitting) return;
    const startIso = toIsoString(newStart);
    if (!startIso) {
      toast.error('Pick a new start date and time.');
      return;
    }
    setSubmitting(true);
    const body = { start: startIso };
    const endIso = toIsoString(newEnd);
    if (endIso) body.end = endIso;
    const res = await schedulerApi.updateAppointment(appointment.id, body);
    setSubmitting(false);
    if (!res?.ok) {
      toastApiError(res?.error, 'Could not reschedule appointment.');
      return;
    }
    toast.success('Appointment rescheduled.');
    onMutated?.(res.data?.appointment);
    onClose?.();
  }, [appointment, newStart, newEnd, submitting, onMutated, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="cancel-reschedule-modal"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-md border-slate-200 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <CardContent className="p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <p className="text-sm font-semibold text-slate-800">
                {appointment?.patient_name || 'Appointment'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="h-7 w-7 rounded-md inline-flex items-center justify-center text-slate-500 hover:bg-slate-100"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              aria-label="Close"
              data-testid="cancel-reschedule-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {!canWrite && (
            <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0" />
              <p className="text-[11px] text-amber-700" data-testid="cr-readonly-banner">
                Read-only role — cancel and reschedule are disabled. Ask a workspace admin to grant the <code className="px-1 rounded bg-amber-100">scheduler:ops:write</code> capability.
              </p>
            </div>
          )}

          <div className="px-4 pt-3 flex gap-1">
            <button
              type="button"
              onClick={() => setTab('reschedule')}
              className={`px-3 py-1.5 rounded-md text-[11px] font-medium ${tab === 'reschedule' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              style={{ border: 'none', cursor: 'pointer' }}
              data-testid="cr-tab-reschedule"
            >
              Reschedule
            </button>
            <button
              type="button"
              onClick={() => setTab('cancel')}
              className={`px-3 py-1.5 rounded-md text-[11px] font-medium ${tab === 'cancel' ? 'bg-red-600 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
              style={{ border: 'none', cursor: 'pointer' }}
              data-testid="cr-tab-cancel"
            >
              Cancel appointment
            </button>
          </div>

          {tab === 'reschedule' && (
            <div className="px-4 py-4 space-y-3" data-testid="cr-reschedule-form">
              <div>
                <Label className="text-[11px] text-slate-600">New start</Label>
                <Input
                  type="datetime-local"
                  value={newStart}
                  onChange={(e) => setNewStart(e.target.value)}
                  data-testid="cr-new-start"
                  className="text-[12px]"
                  disabled={!canWrite || submitting}
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600">New end (optional)</Label>
                <Input
                  type="datetime-local"
                  value={newEnd}
                  onChange={(e) => setNewEnd(e.target.value)}
                  data-testid="cr-new-end"
                  className="text-[12px]"
                  disabled={!canWrite || submitting}
                />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={onClose} data-testid="cr-reschedule-discard">
                  Discard
                </Button>
                <Button
                  size="sm"
                  onClick={handleReschedule}
                  disabled={!canWrite || submitting || !newStart}
                  className="bg-[#111] hover:bg-[#222] text-white gap-1"
                  data-testid="cr-reschedule-submit"
                >
                  {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
                  Reschedule
                </Button>
              </div>
            </div>
          )}

          {tab === 'cancel' && (
            <div className="px-4 py-4 space-y-3" data-testid="cr-cancel-form">
              <div>
                <Label className="text-[11px] text-slate-600">Reason (optional)</Label>
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Patient requested"
                  data-testid="cr-cancel-reason"
                  className="text-[12px]"
                  disabled={!canWrite || submitting}
                />
              </div>
              <label className="flex items-center gap-2 text-[11px] text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={notifyPatient}
                  onChange={(e) => setNotifyPatient(e.target.checked)}
                  data-testid="cr-cancel-notify"
                  disabled={!canWrite || submitting}
                />
                <span>Notify the patient</span>
              </label>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={onClose} data-testid="cr-cancel-keep">
                  Keep appointment
                </Button>
                <Button
                  size="sm"
                  onClick={handleCancel}
                  disabled={!canWrite || submitting}
                  className="bg-red-600 hover:bg-red-700 text-white gap-1"
                  data-testid="cr-cancel-submit"
                >
                  {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
                  Cancel appointment
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CancelRescheduleModal;
